'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

// Google Analytics hook for automatic page view tracking
export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Only track in production
    if (process.env.NODE_ENV !== 'production') return;

    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId) {
      console.warn('GA Measurement ID not found');
      return;
    }

    // Initialize GA4 if not already done
    if (!window.dataLayer) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function() { 
        window.dataLayer.push(arguments); 
      };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        page_path: pathname,
      });
    }

    // Track page view
    const url = pathname + (searchParams.toString() ? `?${searchParams}` : '');
    window.gtag('event', 'page_view', {
      page_path: url,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, searchParams]);
}

// Generic event tracking function
export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (process.env.NODE_ENV === 'production' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  } else {
    console.log('GA Event (dev):', eventName, eventParams);
  }
}

// Conversion funnel tracking functions
export const trackFunnelStep = (step: string, language: 'en' | 'de', additionalParams = {}) => {
  trackEvent('funnel_step', {
    funnel_name: 'pilot_program_signup',
    funnel_step: step,
    language,
    ...additionalParams,
  });
};

// Pilot program specific tracking
export const trackPilotSignup = {
  // Step 1: User views the landing page
  viewLandingPage: (language: 'en\' | \'de', pageType: string) => {
    trackFunnelStep('landing_page_view', language, {
      page_type: pageType,
      timestamp: new Date().toISOString(),
    });
  },

  // Step 2: User opens signup modal
  openModal: (language: 'en\' | \'de', trigger: string) => {
    trackFunnelStep('modal_opened', language, {
      trigger_source: trigger,
    });
    trackEvent('modal_interaction', {
      action: 'open',
      modal_type: 'pilot_signup',
      language,
      trigger_source: trigger,
    });
  },

  // Step 3: User starts filling the form
  startForm: (language: 'en\' | \'de') => {
    trackFunnelStep('form_started', language);
    trackEvent('form_interaction', {
      action: 'start',
      form_type: 'pilot_signup',
      language,
    });
  },

  // Step 4: User submits the form
  submitForm: (language: 'en\' | \'de', formData: { company?: string; industry?: string }) => {
    trackFunnelStep('form_submitted', language, {
      has_company: !!formData.company,
      industry: formData.industry || 'not_specified',
    });
    trackEvent('form_submission', {
      form_type: 'pilot_signup',
      language,
      ...formData,
    });
  },

  // Step 5: Successful submission
  submitSuccess: (language: 'en\' | \'de', userId?: string) => {
    trackFunnelStep('submission_success', language, {
      user_id: userId,
    });
    trackEvent('conversion', {
      conversion_type: 'pilot_signup',
      language,
      value: 1,
    });
  },

  // Error tracking
  submitError: (language: 'en\' | \'de', errorMessage: string) => {
    trackFunnelStep('submission_error', language, {
      error_message: errorMessage,
    });
    trackEvent('form_error', {
      form_type: 'pilot_signup',
      language,
      error_message: errorMessage,
    });
  },

  // Modal close tracking (abandonment)
  closeModal: (language: 'en' | 'de', reason: 'user_action' | 'outside_click' | 'escape') => {
    trackEvent('modal_interaction', {
      action: 'close',
      modal_type: 'pilot_signup',
      language,
      close_reason: reason,
    });
  },
};

// User behavior tracking
export const trackUserBehavior = {
  // Time spent on page
  pageEngagement: (duration: number, language: 'en' | 'de') => {
    trackEvent('user_engagement', {
      engagement_time_msec: duration,
      language,
    });
  },

  // Scroll depth
  scrollDepth: (percentage: number, language: 'en' | 'de') => {
    trackEvent('scroll', {
      percent_scrolled: percentage,
      language,
    });
  },

  // CTA clicks
  ctaClick: (ctaName: string, location: string, language: 'en' | 'de') => {
    trackEvent('cta_click', {
      cta_name: ctaName,
      cta_location: location,
      language,
    });
  },

  // Feature interaction
  featureInteraction: (featureName: string, action: string, language: 'en' | 'de') => {
    trackEvent('feature_interaction', {
      feature_name: featureName,
      interaction_type: action,
      language,
    });
  },

  // Time calculator usage
  calculatorUsage: (hours: number, days: number, language: 'en' | 'de') => {
    trackEvent('calculator_interaction', {
      hours_input: hours,
      days_input: days,
      language,
    });
  },
};

// Landing page performance tracking
export const trackLandingPagePerformance = {
  // Section visibility
  sectionView: (sectionName: string, language: 'en' | 'de') => {
    trackEvent('section_view', {
      section_name: sectionName,
      language,
    });
  },

  // FAQ interaction
  faqInteraction: (question: string, language: 'en' | 'de') => {
    trackEvent('faq_interaction', {
      question,
      language,
    });
  },

  // Pricing section view
  pricingView: (language: 'en' | 'de') => {
    trackEvent('pricing_section_view', {
      language,
    });
  },

  // Social proof interaction
  testimonialView: (language: 'en' | 'de') => {
    trackEvent('testimonial_view', {
      language,
    });
  },
};

// TypeScript declaration for gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}